import React, { useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ViewManager } from '../../../../../core/view-manager';
import { LanguageProvider } from '../../../../../core/language-provider';
import './style.css';

/**
 * SubFieldSelector - Modal dialog for selecting which sub-field to display
 * when an externalEntity field is added to the layout or table.
 */
export default function SubFieldSelector({
  isOpen,
  field,
  onSelect,
  onCancel
}) {
  // Get the related entity and its fields
  const relatedEntityInfo = useMemo(() => {
    if (!field || !field.options?.entity) return null;

    const entityName = field.options.entity;
    const entityProps = ViewManager.getEntityProperties(entityName);

    if (!entityProps?.columns) return null;

    // Get fields from the related entity
    const fields = Object.entries(entityProps.columns)
      .filter(([name, config]) => {
        // Exclude certain field types
        const excluded = ['deleted', 'password'];
        const excludedTypes = ['password', 'file', 'image', 'multipleExternalEntity'];
        return !excluded.includes(name) &&
               !excludedTypes.includes(config.input_type);
      })
      .map(([name, config]) => ({
        name: name,
        label: LanguageProvider.getLabel(entityName, name) || name,
        type: config.input_type,
        isLabelField: name === field.options.labelField
      }))
      .sort((a, b) => {
        // Put the labelField first
        if (a.isLabelField) return -1;
        if (b.isLabelField) return 1;
        return a.label.localeCompare(b.label);
      });

    return {
      entityName,
      entityLabel: LanguageProvider.get(`entity.${entityName}`, entityName),
      fields,
      labelField: field.options.labelField
    };
  }, [field]);

  if (!isOpen || !relatedEntityInfo) return null;

  const handleSelectField = (subField) => {
    onSelect({
      ...field,
      subField: subField.name,
      subFieldLabel: subField.label,
      displayLabel: `${field.label} → ${subField.label}`
    });
  };

  // Quick select the default labelField
  const handleSelectDefault = () => {
    const defaultField = relatedEntityInfo.fields.find(f => f.isLabelField);
    if (defaultField) {
      handleSelectField(defaultField);
    }
  };

  return (
    <div className="subfield-selector-overlay" onClick={onCancel}>
      <div className="subfield-selector-modal" onClick={e => e.stopPropagation()}>
        <div className="subfield-selector-header">
          <div className="subfield-selector-title">
            <FontAwesomeIcon icon="link" />
            <span>Seleziona Campo da Visualizzare</span>
          </div>
          <button className="subfield-close-btn" onClick={onCancel}>
            <FontAwesomeIcon icon="times" />
          </button>
        </div>

        <div className="subfield-selector-info">
          <FontAwesomeIcon icon="info-circle" />
          <span>
            Il campo <strong>{field.label}</strong> è un riferimento a <strong>{relatedEntityInfo.entityLabel}</strong>.
            Seleziona quale campo vuoi visualizzare.
          </span>
        </div>

        {/* Quick select default */}
        {relatedEntityInfo.labelField && (
          <div className="subfield-quick-select">
            <button
              className="subfield-quick-btn"
              onClick={handleSelectDefault}
            >
              <FontAwesomeIcon icon="star" />
              <span>Usa campo predefinito: <strong>{relatedEntityInfo.labelField}</strong></span>
            </button>
          </div>
        )}

        <div className="subfield-list-title">
          <FontAwesomeIcon icon="list" />
          <span>Campi disponibili in {relatedEntityInfo.entityLabel}:</span>
        </div>

        <div className="subfield-list">
          {relatedEntityInfo.fields.map(subField => (
            <button
              key={subField.name}
              className={`subfield-item ${subField.isLabelField ? 'is-default' : ''}`}
              onClick={() => handleSelectField(subField)}
            >
              <div className="subfield-item-name">
                {subField.label}
                {subField.isLabelField && (
                  <span className="subfield-default-badge">predefinito</span>
                )}
              </div>
              <div className="subfield-item-type">{subField.type}</div>
            </button>
          ))}
        </div>

        <div className="subfield-selector-footer">
          <button className="subfield-cancel-btn" onClick={onCancel}>
            Annulla
          </button>
        </div>
      </div>
    </div>
  );
}
